/**
 * Design-skill API client (super-admin White-Label Editor).
 *
 * A design skill is a per-tenant .zip bundle (brand.json + assets + rulebook)
 * that makes agent-generated artifacts match the tenant's brand exactly. These
 * calls hit the super-admin design-skill routes.
 */
import api from '@/services/api';
import { filenameFrom, triggerBlobDownload as saveBlob } from '@/utils/download';

/** Which entrance these calls go through. Super-admins use the super-admin
 * routes; group admins reach the SAME functionality for tenants in their
 * subtree through mirrored /group-admin routes (Admin page > System Design).
 * Same payloads, same responses -- only the path prefix and the authorization
 * scope differ. */
export type DesignSkillApiBase = 'super-admin' | 'group-admin';

export interface DesignSkillAsset {
  logical: string;
  path: string;
  mime: string;
  bytes: number;
  kind: 'asset' | 'font' | 'doc' | 'config';
}

export interface DesignSkillIgnored {
  logical: string;
  reason: string;
}

export interface DesignSkillManifest {
  version: number;
  name: string;
  uploaded_at: string;
  uploaded_by: string | null;
  storage_prefix: string;
  entry_markdown_path: string | null;
  brand_json_present: boolean;
  size_bytes: number;
  asset_index: DesignSkillAsset[];
  ignored: DesignSkillIgnored[];
  // Normalized brand spec — shape mirrors brand.json (kept loose on purpose).
  brand_spec: any | null;
}

export async function getDesignSkill(tenantId: string, base: DesignSkillApiBase = 'super-admin'): Promise<DesignSkillManifest | null> {
  const res = await api.get(`/${base}/tenants/${tenantId}/design-skill`);
  return res.data?.design_skill ?? null;
}

export async function uploadDesignSkill(tenantId: string, file: File, base: DesignSkillApiBase = 'super-admin'): Promise<DesignSkillManifest> {
  const form = new FormData();
  form.append('file', file);
  // Let the browser/axios set the multipart boundary automatically.
  const res = await api.post(`/${base}/tenants/${tenantId}/design-skill`, form);
  return res.data.design_skill as DesignSkillManifest;
}

export async function deleteDesignSkill(tenantId: string, base: DesignSkillApiBase = 'super-admin'): Promise<void> {
  await api.delete(`/${base}/tenants/${tenantId}/design-skill`);
}

/** Download the tenant's bundle as a .zip and hand it to the browser.
 *
 * Goes through the authenticated client rather than a plain `<a href>` because
 * these routes need the JWT + tenant headers -- a bare link would 401.
 */
export async function downloadDesignSkill(tenantId: string, base: DesignSkillApiBase = 'super-admin'): Promise<void> {
  const res = await api.get(`/${base}/tenants/${tenantId}/design-skill/download`, { responseType: 'blob' });
  saveBlob(res.data as Blob, filenameFrom(res.headers, 'design-skill.zip'));
}

/** Fetch a bundle asset (with auth) and return an object URL for <img>. */
export async function fetchDesignAssetUrl(tenantId: string, logical: string, base: DesignSkillApiBase = 'super-admin'): Promise<string> {
  const res = await api.get(`/${base}/tenants/${tenantId}/design-skill/asset`, {
    params: { logical },
    responseType: 'blob',
  });
  return URL.createObjectURL(res.data as Blob);
}

/** Whether `modules.design_skills` is currently ON (resolved) for the tenant. */
export async function getDesignSkillEnabled(tenantId: string, base: DesignSkillApiBase = 'super-admin'): Promise<boolean> {
  if (base === 'group-admin') {
    // Group admins have no general features endpoint -- a narrow, design-skill
    // only route answers the same question for tenants in their subtree.
    const res = await api.get(`/group-admin/tenants/${tenantId}/design-skill/enabled`);
    return !!res.data?.enabled;
  }
  const res = await api.get(`/super-admin/tenants/${tenantId}/features`);
  return !!res.data?.resolved?.['modules.design_skills'];
}

/** Turn the design-skill feature on/off for the tenant. Enabling also flips on
 *  the parent `modules.agent_outputs` (atomic) so agents can actually produce
 *  branded artifacts. Returns the resolved on/off state. */
export async function setDesignSkillEnabled(tenantId: string, enabled: boolean, base: DesignSkillApiBase = 'super-admin'): Promise<boolean> {
  if (base === 'group-admin') {
    // The group-admin route writes the SAME chain server-side; it cannot be
    // used to reach any other feature key.
    const res = await api.patch(`/group-admin/tenants/${tenantId}/design-skill/enabled`, { enabled });
    return !!res.data?.enabled;
  }
  // design_skills is gated by its ancestors (agent_outputs → agents); enabling
  // the whole chain atomically guarantees it actually resolves ON.
  const features = enabled
    ? { 'modules.design_skills': true, 'modules.agent_outputs': true, 'modules.agents': true }
    : { 'modules.design_skills': false };
  const res = await api.patch(`/super-admin/tenants/${tenantId}/features`, { features });
  return !!res.data?.resolved?.['modules.design_skills'];
}

/* --- Default design skill — set on a tenant, INHERITED by its subtree -----
 * Used whenever an artifact would otherwise render plain: the tenant has no
 * design skill of its own, or the user switched branding off. Resolution takes
 * the nearest enabled default walking UP the tenant tree, so several defaults
 * can coexist and a sub-tree can override its parent's. Its enabled flag lives
 * on the manifest (a subtree setting), not on a tenant feature flag.
 */
export interface DefaultDesignSkillManifest extends DesignSkillManifest {
  enabled?: boolean;
}

export interface DefaultDesignSkillState {
  /** The default set ON this tenant (null when it defines none). */
  own: DefaultDesignSkillManifest | null;
  /** What this tenant's artifacts actually inherit today (may be an ancestor's). */
  inherited: DefaultDesignSkillManifest | null;
}

export async function getDefaultDesignSkill(tenantId: string, base: DesignSkillApiBase = 'super-admin'): Promise<DefaultDesignSkillState> {
  const res = await api.get(`/${base}/tenants/${tenantId}/default-design-skill`);
  return { own: res.data?.design_skill ?? null, inherited: res.data?.inherited ?? null };
}

export async function uploadDefaultDesignSkill(
  tenantId: string, file: File, base: DesignSkillApiBase = 'super-admin',
): Promise<DefaultDesignSkillManifest> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post(`/${base}/tenants/${tenantId}/default-design-skill`, form);
  return res.data.design_skill;
}

export async function deleteDefaultDesignSkill(tenantId: string, base: DesignSkillApiBase = 'super-admin'): Promise<void> {
  await api.delete(`/${base}/tenants/${tenantId}/default-design-skill`);
}

/** Download the default bundle set ON this tenant (never the inherited one). */
export async function downloadDefaultDesignSkill(tenantId: string, base: DesignSkillApiBase = 'super-admin'): Promise<void> {
  const res = await api.get(`/${base}/tenants/${tenantId}/default-design-skill/download`, { responseType: 'blob' });
  saveBlob(res.data as Blob, filenameFrom(res.headers, 'default-design-skill.zip'));
}

export async function setDefaultDesignSkillEnabled(
  tenantId: string, enabled: boolean, base: DesignSkillApiBase = 'super-admin',
): Promise<boolean> {
  const res = await api.patch(`/${base}/tenants/${tenantId}/default-design-skill`, { enabled });
  return !!res.data?.design_skill?.enabled;
}

export async function fetchDefaultDesignAssetUrl(
  tenantId: string, logical: string, base: DesignSkillApiBase = 'super-admin',
): Promise<string> {
  const res = await api.get(`/${base}/tenants/${tenantId}/default-design-skill/asset`, {
    params: { logical },
    responseType: 'blob',
  });
  return URL.createObjectURL(res.data as Blob);
}

/* ─── Brand sandbox ──────────────────────────────────────────────────────────
 * Try a bundle in ONE conversation instead of installing it on the tenant.
 * The routes are group-admin only: testing is an admin act on a tenant they
 * manage, and there is no platform-default equivalent to try.
 */

export interface BrandSandboxSummary {
  name: string | null;
  size_bytes: number;
  asset_count: number;
  brand_json_present: boolean;
  ignored_count: number;
  source_file_name: string | null;
  started_at: string | null;
}

export interface BrandSandboxSession {
  conversation_id: string;
  chatbot_id: string;
  chatbot_name: string;
  manifest: BrandSandboxSummary;
}

/** Upload a .zip to TRY. Returns the conversation to open; installs nothing. */
/**
 * This workspace's Brand Book Studio agent, created on first use.
 *
 * Idempotent on the server: an existing authoring agent is returned rather
 * than a second one made. Called when someone asks to start a brand book, so
 * the offer on the System Design page is always true — a workspace nobody
 * had configured by hand used to reach a card that explained it had no such
 * agent and left it at that.
 */
export async function ensureBrandbookStudio(
  tenantId: string,
): Promise<{ chatbot_id: string; chatbot_name: string }> {
  const res = await api.post(`/group-admin/tenants/${tenantId}/brandbook-studio`, {});
  return res.data;
}

export async function startBrandSandbox(tenantId: string, file: File): Promise<BrandSandboxSession> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post(`/group-admin/tenants/${tenantId}/brand-sandbox`, form);
  return res.data as BrandSandboxSession;
}

/** Forget the tested bundle and delete its assets. The conversation stays. */
export async function endBrandSandbox(tenantId: string, conversationId: string): Promise<void> {
  await api.delete(`/group-admin/tenants/${tenantId}/brand-sandbox/${conversationId}`);
}
